import React, { useEffect, useState } from "react";
import "../assets/css/custom.css";
import "../assets/css/client.css";
import "../assets/css/management.css";
import "../assets/css/font-awesome.css";
import "../assets/css/bootstrap.min.css";
import "../assets/css/report.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEllipsisV } from "@fortawesome/free-solid-svg-icons";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import Dropdown from "react-bootstrap/Dropdown";

import { toast } from "react-toastify";
import SelectSearch from "react-select-search";
import "react-select-search/style.css";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import curonext from "../assets/img/CuronextFinal.png";
import { commonAPI } from "../services/CommonAPIService";

const TowerReport = ({ show, onHide, fullscreen }) => {
    // States
    const [loading, setLoading] = useState(false);
    const [towerList, setTowerList] = useState([]);
    const [selectedTower, setSelectedTower] = useState(""); // towerId or ""
    const [selectedTowerName, setSelectedTowerName] = useState("All Towers");
    const [towerReportData, setTowerReportData] = useState([]);
    const [masterData, setMasterData] = useState([]); // unfiltered data for search reset
    const [searchText, setSearchText] = useState("");
    const [userData, setUserData] = useState(null);

    // Fetch Tower Dropdown List
    const getTowerList = async () => {
        const auth = localStorage.getItem("userData");
        if (!auth) return toast.error("User not authenticated");

        const myUserData = JSON.parse(auth);
        setUserData(myUserData);

        try {
            const payload = { urlVariable: null, buildingId: myUserData.buildingId };
            const response = await commonAPI("Tower", undefined, payload);

            if (response?.success) {
                setTowerList(response.data || []);
            } else {
                toast.error("Failed to load tower list");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error loading towers");
        }
    };

    // Fetch & Filter Tower Report Data
    const fetchTowerReport = async () => {
        const auth = localStorage.getItem("userData");
        if (!auth) return;

        const myUserData = JSON.parse(auth);
        setLoading(true);

        try {
            const payload = { urlVariable: null, buildingId: myUserData.buildingId };
            const response = await commonAPI("Tower", undefined, payload);

            if (response?.success && response.data?.length > 0) {
                let filtered = response.data;

                // Apply Tower Filter
                if (selectedTower) {
                    filtered = filtered.filter(item => item.towerId === selectedTower);
                }

                setMasterData(filtered);
                setTowerReportData(filtered);
                setSearchText(""); // Clear search on new filter
            } else {
                setMasterData([]);
                setTowerReportData([]);
                toast.info("No tower data found");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load report data");
            setMasterData([]);
            setTowerReportData([]);
        } finally {
            setLoading(false);
        }
    };

    // Search within current data
    const handleSearch = (text) => {
        setSearchText(text);

        if (!text.trim()) {
            setTowerReportData(masterData);
            return;
        }

        const query = text.toLowerCase();
        const filtered = masterData.filter(item =>
            Object.values(item).some(val =>
                val !== null && val !== undefined &&
                val.toString().toLowerCase().includes(query)
            )
        );

        setTowerReportData(filtered);
    };

    // Excel Export
    const exportToExcel = () => {
        if (towerReportData.length === 0) {
            toast.warning("No data to export");
            return;
        }

        const dataToExport = towerReportData.map((item, idx) => ({
            "S.No": idx + 1,
            "Tower Name": item.towerName || "-",
            "Tower Code": item.towerCode || "-",
            "Total Floors": item.totalFloors || 0,
            "Total Flats": item.totalFlats || 0,
            "Status": item.isActive ? "Active" : "Inactive"
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        XLSX.utils.book_append_sheet(wb, ws, "Tower Report");
        XLSX.writeFile(wb, `Tower_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
        toast.success("Excel exported successfully");
    };

    // PDF Export
    const exportToPDF = () => {
        if (towerReportData.length === 0) {
            toast.warning("No data to export");
            return;
        }

        setLoading(true);
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();

        // Header
        if (userData?.logo) {
            doc.addImage(`data:image/jpeg;base64,${userData.logo}`, "JPEG", 10, 8, 25, 15);
        }
        doc.addImage(curonext, "PNG", pageWidth - 45, 8, 35, 12);

        doc.setFontSize(14).setFont("helvetica", "bold");
        doc.text(userData?.buildingName || "Building", pageWidth / 2, 15, { align: "center" });

        doc.setFontSize(10).setFont("helvetica", "normal");
        doc.text(`${userData?.addressLine1 || ""} ${userData?.addressLine2 || ""}`, pageWidth / 2, 22, { align: "center" });
        doc.text(`${userData?.city || ""}, ${userData?.state || ""}`, pageWidth / 2, 27, { align: "center" });

        doc.setFontSize(16).setFont("helvetica", "bold");
        doc.text("Tower Report", pageWidth / 2, 40, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Filter: ${selectedTowerName}`, pageWidth / 2, 48, { align: "center" });

        // Table
        const tableData = towerReportData.map((item, idx) => [
            idx + 1,
            item.towerName || "-",
            item.towerCode || "-",
            item.totalFloors || 0,
            item.totalFlats || 0,
            item.isActive ? "Active" : "Inactive"
        ]);

        doc.autoTable({
            head: [["S.No", "Tower Name", "Tower Code", "Total Floors", "Total Flats", "Status"]],
            body: tableData,
            startY: 55,
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255 },
            alternateRowStyles: { fillColor: [245, 250, 255] },
            foot: [[``, ``, ``, ``, `Total: ${towerReportData.length} Towers`, ``]],
            footStyles: { fillColor: [52, 73, 94], textColor: 255, fontStyle: "bold" }
        });

        doc.save(`Tower_Report_${new Date().toISOString().slice(0,10)}.pdf`);
        setLoading(false);
        toast.success("PDF exported successfully");
    };

    // Load tower list when modal opens
    useEffect(() => {
        if (show) {
            getTowerList();
        }
    }, [show]);

    // Auto-fetch when tower selection changes (optional smooth UX)
    useEffect(() => {
        if (show && towerList.length > 0) {
            fetchTowerReport();
        }
        // eslint-disable-next-line
    }, [selectedTower]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            fullscreen={fullscreen}
            
                        dialogclassName="modal-100w clientmodal"
                        class="modal-dialog modal-fullscreen"
        >
            <Modal.Header closeButton className="headerMDL">
                <Modal.Title>Tower Report</Modal.Title>
            </Modal.Header>

            <div className="textFromBehind">
                <p>View comprehensive details of all towers with advanced filtering and export options.</p>
            </div>

            <Modal.Body className="p-4">
                <div className="container-fluid">

                    {/* Filters & Export */}
                    <div className="row align-items-end mb-4">
                        <div className="col-lg-9 col-md-8">
                            <div className="row g-3">
                                <div className="col-lg-4 col-md-5">
                                    <label className="form-label syntax fw-bold">Tower</label>
                                    <SelectSearch
                                        options={[
                                            { value: "", name: "All Towers" },
                                            ...towerList.map(t => ({
                                                value: t.towerId,
                                                name: t.towerName
                                            }))
                                        ]}
                                        value={selectedTower}
                                        search
                                        placeholder="Select Tower"
                                        onChange={(value, { name }) => {
                                            setSelectedTower(value);
                                            setSelectedTowerName(name || "All Towers");
                                        }}
                                    />
                                </div>

                                <div className="col-lg-3 col-md-4">
                                    <label className="form-label syntax invisible">Action</label>
                                    <button
                                        className="btn reportbtn w-100"
                                        onClick={fetchTowerReport}
                                        disabled={loading}
                                    >
                                        {loading ? "Loading..." : "Apply Filter"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-3 col-md-4 text-end">
                            <Dropdown>
                                <Dropdown.Toggle variant="success" className="px-4">
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                    <Dropdown.Item onClick={exportToExcel}>
                                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                                        Export to Excel
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={exportToPDF}>
                                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                                        Export to PDF
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>

                    {/* Search Box */}
                    {towerReportData.length > 0 && (
                        <div className="row mb-3">
                            <div className="col-md-5">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search in results..."
                                    value={searchText}
                                    onChange={(e) => handleSearch(e.target.value)}
                                />
                            </div>
                            <div className="col-md-7 text-md-end pt-2">
                                <small className="text-muted">
                                    Showing {towerReportData.length} of {masterData.length} towers
                                </small>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    <div className="row">
                        <div className="col-12">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : towerReportData.length > 0 ? (
                                <div className="table-responsive" style={{ maxHeight: "calc(100vh - 380px)" }}>
                                    <Table striped bordered hover className="table align-middle">
                                        <thead className="table-primary">
                                            <tr>
                                                <th width="8%">#</th>
                                                <th>Tower Name</th>
                                                <th>Tower Code</th>
                                                <th>Total Floors</th>
                                                <th>Total Flats</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {towerReportData.map((item, index) => (
                                                <tr key={item.towerId || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{item.towerName}</td>
                                                    <td>{item.towerCode}</td>
                                                    <td>{item.totalFloors}</td>
                                                    <td>{item.totalFlats}</td>
                                                    <td>
                                                        <Badge bg={item.isActive ? "success" : "danger"}>
                                                            {item.isActive ? "Active" : "Inactive"}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-5 bg-light rounded">
                                    <p className="text-muted fs-5">
                                        No tower data available. Try adjusting the filter.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default TowerReport;